'use client';

import { rebuildClipsApi } from '@repo/sdk';
import { Button } from '@repo/ui';
import type { JSX } from 'react';
import { useCallback, useState } from 'react';
import { PipelineStepDialog } from './pipeline-step-dialog';
import { StepLogPanel } from './steps/step-log-panel';
import type { IStepActionStatus } from './use-step-action';

// Enqueue is fast (one batched /crop call per page); the crop + reframe + upload run in the
// background on Snow Titan. Loop pages by cursor here until drained.
const REQUEST_TIMEOUT_MS = 120_000;

async function runRebuild(append: (line: string) => void): Promise<void> {
  let cursor: string | undefined;
  let scanned = 0;
  let enqueued = 0;
  let duplicate = 0;
  let skipped = 0;
  let page = 0;

  do {
    const res = await rebuildClipsApi(cursor ? { cursor } : undefined, {
      timeout: REQUEST_TIMEOUT_MS,
    });
    const data = res.data?.data;
    if (!data) {
      break;
    }
    scanned += data.scanned;
    enqueued += data.enqueued;
    duplicate += data.duplicate;
    skipped += data.skipped;
    page += 1;
    append(
      `Page ${page}: enqueued ${data.enqueued}, duplicate ${data.duplicate}, skipped ${data.skipped}  (running: enqueued ${enqueued}/${scanned}, skipped ${skipped}; ~${data.total ?? 0} still need a rebuild)\n`,
    );
    const next = data.nextCursor;
    if (!next || next === cursor) {
      break;
    }
    cursor = next;
  } while (cursor);

  append(
    `\nDone. Enqueued ${enqueued} crops from source (duplicate ${duplicate}, skipped ${skipped}). Snow Titan now re-crops + reframes + uploads them in the background. Skipped = source video missing (e.g. Lord of the Rings) - those cannot be rebuilt.\n`,
  );
}

export function RebuildClipsButton(): JSX.Element {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<IStepActionStatus>('idle');
  const [log, setLog] = useState('');

  const run = useCallback(async () => {
    setStatus('running');
    setLog('Re-cropping clips from their episode source on Snow Titan…\n');
    const append = (line: string): void => setLog((p) => p + line);
    try {
      await runRebuild(append);
      setStatus('done');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      append(`\n[error] ${message}\nProgress is saved; click again to resume.\n`);
      setStatus('error');
    }
  }, []);

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
        Rebuild clips from source
      </Button>
      <PipelineStepDialog
        open={open}
        onOpenChange={setOpen}
        title="Rebuild clips from source"
        description="For deliverable clips that have NO vertical anywhere (base master lost on disk, so a reframe can't help), re-crop from the original episode video on Snow Titan. The crop pipeline regenerates the base + vertical and uploads both. Clips whose source video is gone (e.g. Lord of the Rings) are skipped and need unpublishing instead. Idempotent and safe to re-run."
        primaryAction={{
          label: 'Rebuild all',
          onClick: run,
          pending: status === 'running',
        }}
        body={<StepLogPanel status={status} log={log} />}
      />
    </>
  );
}
